import _ from "lodash";

import loadData from "../loadData";
import { appStateActionTypes } from "./appState";
import { autoUpdatesActionTypes } from "./autoUpdates";
import deriveDisplayData from "./display/reducer";
import errorLogger, { handleErrorInReducer } from "./errorLogger";
import { filterActionTypes } from "./filters";
import { mobileScreenActionTypes } from "./mobileScreen";
import { preferencesActionTypes } from "./preferences";
import { printOptionsActionTypes } from "./printOptions";
import { registrationActionTypes } from "./registration";
import rootActionTypes from "./rootActionTypes";
import { exportCsvFile, exportTsfFile, transferPersistentState, updateFile } from "./rootReducer.shared";
import { syncActionTypes } from "./sync";
import { chainedReducer } from "./timelineReducer.readonly";
import { tutorialActionTypes } from "./tutorial";
import { uiStateActionTypes } from "./uiState";
import undoRedo from "./undoRedo.readonly";
import { welcomeActionTypes } from "./welcome";

// The list of actions that are ignored by Undo/Redo.
const ignoreActions = _.union(
  _.values(appStateActionTypes),
  _.values(uiStateActionTypes),
  _.values(preferencesActionTypes),
  _.values(printOptionsActionTypes),
  _.values(autoUpdatesActionTypes),
  _.values(registrationActionTypes),
  _.values(filterActionTypes),
  _.values(welcomeActionTypes),
  _.values(syncActionTypes),
  _.values(mobileScreenActionTypes),
  _.values(tutorialActionTypes)
);

const undoImmunityKeys = ["preferences", "printOptions", "uiState", "autoUpdates", "sync", "tutorial", "registration"];

export const validatedReducer = chainedReducer;

// Add derived data into the state that we want to be able to cache, where we cannot use reselect because
// we need to specially optimise it by knowing the action that has been performed and therefore which items
// might need to be re-evaluated
export const displayCacheReducer = deriveDisplayData(chainedReducer);

// Add undo/redo support to the application. This wraps the chainedReducer so that
// BATCH operations are undone/redone as a whole.
const undoableTimelineApp = undoRedo(displayCacheReducer, {
  ignoreActions,
  undoImmunityKeys
});

// Add error logging and crash protection, where any redux action that induces an error within the reducer
// will be rolled back to the previous state.
const errorLoggingReducer = errorLogger(undoableTimelineApp);

// For some reason, we can't seem to wrap this root reducer in errorLogger, in the same way, so have
// added some extra error handling there.
const readonlyRootReducer = (state, action) => {
  let newState = state;

  try {
    switch (action.type) {
      case rootActionTypes.LOAD_FILE:
      case rootActionTypes.LOAD_SAMPLE: {
        // If the user chooses "LOAD_FILE" then we want to replace our entire store
        // with this new data.
        // Due to property name or structure changes
        // We fix the data object and Records here

        newState = loadData.plainJsToTimelineRecord(action.data, action.charWidthMap);
        newState = transferPersistentState(newState, state);
        break;
      }
      case rootActionTypes.UPDATE_FILE: {
        newState = updateFile(newState, action);
        break;
      }
      case rootActionTypes.EXPORT_FILE_TSF: {
        const {
          attachmentPath,
          disableFiltering,
          disableFilteringMore,
          disableSplitView,
          disableTimelineZoom,
          skin
        } = action;
        exportTsfFile(
          state.present,
          attachmentPath,
          disableFiltering,
          disableFilteringMore,
          disableSplitView,
          disableTimelineZoom,
          skin
        );
        break;
      }
      case rootActionTypes.EXPORT_FILE_CSV: {
        const { delimiter, typeIds, isSelectedItemsOnly } = action;
        exportCsvFile(state.present, delimiter, typeIds, isSelectedItemsOnly);
        break;
      }
      default:
        break;
    }
  } catch (err) {
    // Caught an error inside our root.
    // Reset state back to the start.
    newState = handleErrorInReducer(null, state, action, err);
  }

  // Run and return our normal reducers
  const result = errorLoggingReducer(newState, action);
  return result;
};

export default readonlyRootReducer;
